/**
 * 应用外壳布局
 * TopNavBar + main content + Footer 的整体布局容器
 * 在此处触发全局预加载（partnerInfo / dashboardStats / announcements / activities），
 * 避免在 DashboardPage 等子页面中重复调用 + 切换页面数据丢失
 *
 * 同时承担认证路由守卫：未完成认证时弹窗提示并自动跳转到认证进度页
 */
import { useEffect, useState } from 'react';
import { Alert, Box, Snackbar } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { TopNavBar } from './TopNavBar';
import { Footer } from './Footer';
import { LAYOUT } from '@/theme/tokens';
import { usePartnerStore } from '@/stores/partnerStore';
import { useAuthStore } from '@/stores/authStore';

/**
 * AppShell 组件
 * 整体布局：顶部导航栏 + 内容区 + 页脚
 * 已登录后只预加载一次（受 store 内 globalLoaded / 缓存命中保护）
 * 认证流程页面（/certification/*）不预加载首页数据，避免触发未认证拦截
 */
export function AppShell(): JSX.Element {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const preloadGlobal = usePartnerStore((s) => s.preloadGlobal);
  const partnerInfo = usePartnerStore((s) => s.partnerInfo);
  const location = useLocation();
  const navigate = useNavigate();
  const isCertificationPage = location.pathname.startsWith('/certification');
  const [certAlert, setCertAlert] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !isCertificationPage) {
      preloadGlobal();
    }
  }, [isAuthenticated, preloadGlobal, isCertificationPage]);

  // 认证路由守卫：未完成认证/未授牌 → 弹窗 + 自动跳转到认证页
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!partnerInfo) return;
    if (isCertificationPage) return;
    const status = partnerInfo.certificationStatus;
    if (status === undefined || status === null) return;
    // 认证状态枚举（与后端一致）：3=已认证（授牌通过）才放行
    const isCertComplete = status === 3;
    if (isCertComplete) return;
    setCertAlert(true);
    // 立即跳转（无延迟），让用户体验更连贯
    navigate('/certification/progress', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, partnerInfo, location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* 顶部导航栏 */}
      <TopNavBar />

      {/* 主内容区 */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: `${LAYOUT.maxContentWidth}px`,
          width: '100%',
          mx: 'auto',
          px: { xs: 1.5, sm: 2, md: `${LAYOUT.contentPadding}px` },
          py: { xs: 2, md: 3 },
        }}
      >
        <Outlet />
      </Box>

      {/* 页脚 */}
      <Footer />

      {/* 认证未完成 Snackbar */}
      <Snackbar
        open={certAlert}
        autoHideDuration={2500}
        onClose={() => setCertAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="warning" sx={{ width: '100%', fontWeight: 500 }}>
          账号尚未完成合作伙伴认证，正在跳转到认证页面…
        </Alert>
      </Snackbar>
    </Box>
  );
}
